import { useCallback, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle2,
  Github,
  Sun,
  Moon,
  Monitor,
  RotateCcw,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useTheme, emptyColors } from '@/components/theme/theme-provider';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { PageHeader } from '@/components/layout/PageHeader';
import { LanguageSelector } from '@/components/i18n/LanguageSelector';
import { UserAiProviderSettings } from '@/components/user-ai-provider-settings.component';

const DEFAULTS = {
  light: { accent: '#171717', background: '#ffffff', foreground: '#0a0a0a' },
  dark: { accent: '#fafafa', background: '#0a0a0a', foreground: '#fafafa' },
};

function ColorRow({
  label,
  value,
  defaultValue,
  onChange,
}: {
  label: string;
  value: string | undefined;
  defaultValue: string;
  onChange: (hex: string) => void;
}) {
  const display = value || defaultValue;
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-sm font-medium">{label}</span>
      <div className="flex items-center gap-2">
        <label className="relative cursor-pointer">
          <input
            type="color"
            value={display}
            onChange={(e) => onChange(e.target.value)}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          />
          <span
            className="block h-6 w-6 rounded-full border border-border shadow-sm"
            style={{ backgroundColor: display }}
          />
        </label>
        <span className="w-16 text-right font-mono text-xs uppercase text-muted-foreground">
          {display}
        </span>
      </div>
    </div>
  );
}

function ThemeSection({
  title,
  icon,
  colors,
  defaults,
  onChange,
  labels,
}: {
  title: string;
  icon: ReactNode;
  colors: { accent?: string; background?: string; foreground?: string };
  defaults: { accent: string; background: string; foreground: string };
  onChange: (key: 'accent' | 'background' | 'foreground', hex: string) => void;
  labels: { accent: string; background: string; foreground: string };
}) {
  return (
    <div className="min-w-[240px] flex-1">
      <div className="mb-3 flex items-center gap-2">
        {icon}
        <span className="text-sm font-semibold">{title}</span>
      </div>
      <div className="space-y-0 divide-y">
        <ColorRow
          label={labels.accent}
          value={colors.accent}
          defaultValue={defaults.accent}
          onChange={(v) => onChange('accent', v)}
        />
        <ColorRow
          label={labels.background}
          value={colors.background}
          defaultValue={defaults.background}
          onChange={(v) => onChange('background', v)}
        />
        <ColorRow
          label={labels.foreground}
          value={colors.foreground}
          defaultValue={defaults.foreground}
          onChange={(v) => onChange('foreground', v)}
        />
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const { user } = useAuth();
  const { t } = useTranslation(['common', 'settings']);
  const { theme, setTheme, customColors, setCustomColors } = useTheme();

  const updateColor = useCallback(
    (
      mode: 'light' | 'dark',
      key: 'accent' | 'background' | 'foreground',
      hex: string,
    ) => {
      setCustomColors({
        ...customColors,
        [mode]: { ...customColors[mode], [key]: hex },
      });
    },
    [customColors, setCustomColors],
  );

  const resetColors = useCallback(() => {
    setCustomColors(emptyColors);
  }, [setCustomColors]);

  const hasCustom = !!(
    customColors.light.accent ||
    customColors.light.background ||
    customColors.light.foreground ||
    customColors.dark.accent ||
    customColors.dark.background ||
    customColors.dark.foreground
  );

  const fieldLabels = {
    accent: t('settings:accent'),
    background: t('settings:background'),
    foreground: t('settings:foreground'),
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('settings:title')}
        description={t('settings:description')}
      />

      <div className="max-w-2xl space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('settings:languageTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {t('settings:languageDescription')}
            </p>
            <LanguageSelector />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <CardTitle>{t('settings:theme')}</CardTitle>
            <div className="flex items-center gap-1">
              <Button
                variant={theme === 'light' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setTheme('light')}
                className="h-8 gap-1.5"
              >
                <Sun className="h-3.5 w-3.5" />
                {t('settings:light')}
              </Button>
              <Button
                variant={theme === 'dark' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setTheme('dark')}
                className="h-8 gap-1.5"
              >
                <Moon className="h-3.5 w-3.5" />
                {t('settings:dark')}
              </Button>
              <Button
                variant={theme === 'system' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setTheme('system')}
                className="h-8 gap-1.5"
              >
                <Monitor className="h-3.5 w-3.5" />
                {t('settings:system')}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-6 sm:flex-row sm:gap-8">
              <ThemeSection
                title={t('settings:lightTheme')}
                icon={<Sun className="h-4 w-4 text-muted-foreground" />}
                colors={customColors.light}
                defaults={DEFAULTS.light}
                onChange={(key, hex) => updateColor('light', key, hex)}
                labels={fieldLabels}
              />
              <Separator
                orientation="vertical"
                className="hidden h-auto self-stretch sm:block"
              />
              <Separator className="sm:hidden" />
              <ThemeSection
                title={t('settings:darkTheme')}
                icon={<Moon className="h-4 w-4 text-muted-foreground" />}
                colors={customColors.dark}
                defaults={DEFAULTS.dark}
                onChange={(key, hex) => updateColor('dark', key, hex)}
                labels={fieldLabels}
              />
            </div>
            {hasCustom && (
              <div className="mt-4 border-t pt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={resetColors}
                  className="gap-1.5"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  {t('settings:resetDefaults')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('settings:account')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center gap-4">
              {user?.avatar ? (
                <img
                  src={user.avatar}
                  alt={user.name || t('common:unknownUser')}
                  className="h-12 w-12 rounded-full"
                />
              ) : (
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                  <span className="text-lg font-medium text-muted-foreground">
                    {user?.name?.charAt(0)?.toUpperCase() || '?'}
                  </span>
                </div>
              )}
              <div>
                <p className="text-lg font-medium">
                  {user?.name || t('common:noName')}
                </p>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
              </div>
            </div>

            <Separator />

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  {t('settings:provider')}
                </span>
                <span className="text-sm font-medium capitalize">
                  {user?.provider}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  {t('settings:role')}
                </span>
                <Badge variant="secondary" className="capitalize">
                  {user?.role}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  {t('settings:status')}
                </span>
                {user?.isApproved ? (
                  <Badge variant="success">{t('settings:approved')}</Badge>
                ) : (
                  <Badge variant="warning">
                    {t('settings:pendingApproval')}
                  </Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('settings:apiAccess')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2 text-success">
              <CheckCircle2 className="h-5 w-5 text-success" />
              <span>{t('settings:apiProxyMessage')}</span>
            </div>
            <p className="mt-4 text-sm text-muted-foreground">
              {t('settings:apiProxyDescription')}
            </p>
            <UserAiProviderSettings />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('settings:githubIntegration')}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4 text-sm text-muted-foreground">
              {t('settings:githubDescription')}
            </p>
            {user?.hasGithubToken ? (
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 text-success">
                  <CheckCircle2 className="h-5 w-5" />
                  <span className="text-sm font-medium">
                    {t('settings:githubConnected')}
                  </span>
                </div>
              </div>
            ) : (
              <Button
                variant="outline"
                className="gap-2"
                onClick={() => {
                  window.location.href = '/api/auth/github/link';
                }}
              >
                <Github className="h-4 w-4" />
                {t('settings:connectGithub')}
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
